<template>
    <div>
        <div
            class="app-notification-overlay"
            :class="{ show: isOpen }"
            @click.stop="close"
        ></div>
        <div
            class="app-notification"
            :class="[
                { show: isOpen },
                isEnhancedLayout ? 'enhanced-layout' : 'classic-layout'
            ]"
        >
            <!-- panel title -->
            <div class="title">
                <h4 class="m-0">Notifications</h4>
                <i class="fas fa-times cursor-pointer" @click.stop="close"></i>
            </div>

            <!-- 'just receive' banner -->
            <div
                :class="{
                    'just-received': true,
                    show: isJustReceived
                }"
                @click="refresh"
            >
                <span>You have new notification(s)</span>
                <a href="#" @click.prevent>
                    <i class="fa fa-sync-alt"></i> Refresh
                </a>
            </div>

            <!-- dismiss all prompt -->
            <div
                :class="{
                    'dismiss-all-confirm': true,
                    show: isShowPromptDismissAll
                }"
            >
                <div style="flex: 1;">
                    Are you sure you want to dismiss all notifications?
                    <br />
                    This action cannot be undone.
                </div>
                <div v-if="isDismissingAll">
                    <moon-loader color="black" size="20px" />
                </div>
                <button
                    v-if="!isDismissingAll"
                    class="yes"
                    @click="preDismissAll"
                >
                    Yes
                </button>
                <button
                    v-if="!isDismissingAll"
                    @click="isShowPromptDismissAll = false"
                >
                    Cancel
                </button>
            </div>

            <!-- filter and search bar -->
            <filter-search
                v-if="enableFilterSearch"
                ref="filterSearch"
                :isFetching="isFetchingFilter"
                :icons="icons"
                :filterTypes="filterTypesHasCount"
                :selectedFilters="selectedFilterTypes"
                @select-filter-type="handleSelectType"
                @search="search"
                @close-search="closeSearch"
                @start-search-mode="startSearchMode"
                @clear-search-query="clearSearchQuery"
                @dismiss-all="dismissAll"
                @clear-filter="clearFilter"
                @clear-filter-search="clearFilterSearch"
                :is-dismissing-all="isDismissingAll"
                :currentListCount="list.length"
                :isSearchMode="isSearchMode"
                :checkedAll="isCheckedAll"
                @check-all="handleCheckAll"
                :checkedItems="checkedItemIds"
                :isDisableDismiss="isDisableDismiss"
            />

            <!-- skeleton loading -->
            <div
                v-if="isFetching || (isFilterMode && isFetchingFilter)"
                class="preloader"
            >
                <div class="skeleton" />
                <div class="skeleton" style="animation-delay: 250ms;" />
                <div class="skeleton" style="animation-delay: 500ms;" />
                <div class="skeleton" style="animation-delay: 750ms;" />
            </div>
            <!-- no results (search) -->
            <div
                v-else-if="list.length === 0 && !isFetching && isSearchMode"
                class="content-holder empty no-result"
            >
                <p class="message">No result for "{{ searchValue }}".</p>
            </div>
            <!-- no notification available -->
            <div
                v-else-if="
                    list.length === 0 &&
                    !isFetching &&
                    !isFilterMode &&
                    !isSearchMode
                "
                class="content-holder empty"
            >
                <p class="message">
                    You don't have any new notifications<br />for the past 30
                    days.
                </p>
            </div>
            <!-- notification item list -->
            <div
                v-else
                class="content-holder"
                @scroll="checkScroll"
                ref="contentScrollable"
            >
                <notification-item
                    v-for="item in list"
                    :key="item.id"
                    :type="item.type.type"
                    :label="item.type.label"
                    :content="item.content"
                    :icon="icons[item.type.type]"
                    :time="_fromDateCarbon(item.create_at).toRelative()"
                    :item-id="item.id"
                    @dismiss-item="dismissItem"
                    @convert-to-task="convertToTask"
                    @view-task="goToTask"
                    :preparingModalTask="preparingModalTaskId === item.id"
                    @check="registerCheckItem"
                    :isChecked="checkedItemIds.includes(item.id)"
                    :taskInfo="item.task"
                    @link-clicked="onLinkClicked"
                />
                <div class="loading-next">
                    <span v-if="isLastPage">
                        <span v-if="isSearchMode || isFilterMode">
                            End of results.
                        </span>
                        <span v-else>
                            That's all your notifications<br />from the last 30
                            days.
                        </span>
                    </span>
                    <span v-else-if="isFetchingNext">Loading...</span>
                </div>
            </div>
        </div>
        <modal-task
            ref="modaltask"
            :is-notification="true"
            @reload="dismissItem"
        />
    </div>
</template>

<script>
import NotificationItem from "./item.vue";
import FilterSearch from "./filter.vue";
import NotificationService from "@/services/notification.js";
import ModalTask from "@/components/tasks/task-update-modal";
import ServicePatient from "@/services/patient.js";
import ServiceClient from "@/services/client.js";
import ServiceContact from "@/services/contacts.js";
import notificationIcons from "@/module/commons/notification-icons.js";
import MoonLoader from "vue-spinner/src/MoonLoader.vue";

/**
 * let this value be high. don't set a value below 10. because then the lazy load won't work.
 * lazy load only functions when there is scrolling. so, 30 is a good default value
 * because the first initial load will immediately have many (if there are any).
 */
const PER_PAGE = 30;

export default {
    components: {
        NotificationItem,
        FilterSearch,
        ModalTask,
        MoonLoader
    },
    data() {
        return {
            isFetching: false,
            isFetchingNext: false,
            isFetchingFilter: false,
            isSearchMode: false,
            searchValue: "",
            list: [],
            originalList: [], // the list before a search occur
            meta: {
                currentPage: 1,
                lastPage: -1 // the -1 is intentional. the actual value will be set after the initial fetch
            },
            icons: notificationIcons, // icons are defined here as they are being used by the child components
            filterTypes: [],
            selectedFilterTypes: [],
            isDismissingAll: false,
            preparingModalTaskId: null,
            isJustReceived: false,
            isShowPromptDismissAll: false,
            isCheckedAll: false,
            checkedItemIds: []
        };
    },
    computed: {
        enableFilterSearch() {
            if (this.isShowPromptDismissAll) {
                return false;
            }

            if (
                this.list.length > 0 ||
                this.isFilterMode ||
                this.isSearchMode
            ) {
                return true;
            }

            return false;
        },
        isLastPage() {
            return this.meta.currentPage === this.meta.lastPage;
        },
        isFilterMode() {
            return this.selectedFilterTypes.length > 0;
        },
        filterTypesHasCount() {
            return this.filterTypes.filter(cat => cat.count > 0);
        },
        isDisableDismiss() {
            return this.list.length === 0;
        },
        isEnhancedLayout() {
            return this.$store.state.isEnhancedLayout;
        },
        isOpen() {
            return this.$store.state.isAppNotificationOpen;
        }
    },
    watch: {
        checkedItemIds(values = []) {
            if (values.length > 0 && values.length === this.list.length) {
                this.isCheckedAll = true;
            } else {
                this.isCheckedAll = false;
            }
        },
        isOpen(newVal) {
            if (newVal === true) {
                this.show();
            }
        }
    },
    sockets: {
        "v-broadcast-all"(params) {
            if (
                params.action === "notification-alert:update-count" &&
                params.userId === this.$root.currentActor.id
            ) {
                if (!params.type) {
                    return;
                }
                if (this.isOpen) {
                    this.isJustReceived = true;
                }
            }
        }
    },
    methods: {
        goToTask(statusId, taskId) {
            let tid = taskId ? `&id=${taskId}` : "";
            this.$router.push(`/schedule/tasks?task_status=${statusId}${tid}`);
            this.close();
        },
        show() {
            this.fetchInitialList();
        },
        close() {
            // prevent close if in process of dismissing all
            if (this.isDismissingAll) {
                return;
            }
            this.reset();
            this.$store.commit("toggleAppNotification", false);
        },
        reset() {
            this.filterTypes = [];
            this.selectedFilterTypes = [];
            this.isSearchMode = false;
            this.searchValue = "";
            this.list = [];
            this.isDismissingAll = false;
            this.isJustReceived = false;
            this.isShowPromptDismissAll = false;
            this.clearCheckboxSelection();
            this.resetScrollPosition();
        },
        clearCheckboxSelection() {
            this.checkedItemIds = [];
            this.isCheckedAll = false;
        },
        refresh() {
            this.reset();
            this.fetchInitialList();
        },
        resetScrollPosition() {
            if (this.$refs.contentScrollable) {
                this.$refs.contentScrollable.scrollTop = 0;
            }
        },
        async fetchInitialList(showSkeleton = true) {
            try {
                this.isFetching = showSkeleton;
                let res = await this.fetchList(1);
                this.isFetching = false;
                this.list = res?.data?.response?.resource ?? [];
                this.originalList = [...this.list];
                this.filterTypes = res?.data?.response?.type_count;
                this.meta = {
                    currentPage: 1,
                    lastPage: res?.data?.response?.meta?.lastPage
                };
            } catch (error) {
                this.isFetching = false;
            }
        },
        async fetchNext() {
            if (this.isFetchingNext) {
                return; // prevent multiple requests
            }

            try {
                this.isFetchingNext = true;
                let currentPage = this.meta.currentPage;
                let nextPage = currentPage + 1;
                let res = await this.fetchList(nextPage);
                this.meta = {
                    currentPage: nextPage,
                    lastPage: res?.data?.response?.meta?.lastPage
                };

                let newList = res?.data?.response?.resource ?? [];
                this.list = [...this.list, ...newList]; // append list
                this.isFetchingNext = false;
            } catch (error) {
                this.isFetchingNext = false;
            }
        },
        async fetchList(page, typeIds = [], searchValue = "", perPage) {
            let params = {
                actor_id: this.$root.currentActor.id,
                perPage: perPage || PER_PAGE,
                page: page
            };

            if (searchValue !== "") {
                params.search = searchValue;
            }

            if (typeIds.length > 0) {
                params.type = typeIds;
            }

            return await NotificationService.getNotifications(params);
        },
        checkScroll(event) {
            let target = event.target;
            let modScrollHeight = target.scrollHeight - 5; // ensure the scrollbar is always beyond this height, even -1, -2
            if (target.scrollTop + target.clientHeight >= modScrollHeight) {
                if (!this.isLastPage) {
                    this.fetchNext();
                }
            }
        },
        /**
         * select category (filter)
         */
        handleSelectType(type) {
            // prevent multiple
            if (this.isFetchingFilter) {
                return;
            }

            let arr = [...this.selectedFilterTypes];
            if (arr.includes(type)) {
                arr = arr.filter(t => t !== type);
            } else {
                arr.push(type);
            }

            this.selectedFilterTypes = arr;
            this.fetchWithFilter(arr);
            this.clearCheckboxSelection();
        },
        getFilterIdsByType(types = []) {
            let ids = [];
            for (let type of types) {
                let record = this.filterTypes.find(f => f.type === type);
                if (record) {
                    ids.push(record.id);
                }
            }
            return ids;
        },
        async fetchWithFilter(types = []) {
            try {
                let ids = this.getFilterIdsByType(types); // map types with ids
                this.isFetchingFilter = true;
                let res = await this.fetchList(1, ids);
                this.list = res?.data?.response?.resource ?? [];
                this.filterTypes = res?.data?.response?.type_count;
                this.meta = {
                    currentPage: 1,
                    lastPage: res?.data?.response?.meta?.lastPage
                };
                this.isFetchingFilter = false;
                this.resetScrollPosition();
            } catch (error) {
                this.isFetchingFilter = false;
            }
        },
        async search(query = "") {
            try {
                this.isFetching = true;
                this.searchValue = query;
                let res;

                if (this.selectedFilterTypes.length > 0) {
                    let typeIds = this.getFilterIdsByType(
                        this.selectedFilterTypes
                    );
                    res = await this.fetchList(1, typeIds, query);
                } else {
                    res = await this.fetchList(1, [], query);
                }

                this.isFetching = false;
                this.list = res?.data?.response?.resource ?? [];
                this.meta = {
                    currentPage: 1,
                    lastPage: res?.data?.response?.meta?.lastPage
                };
            } catch (error) {
                this.isFetching = false;
            }
        },
        closeSearch() {
            this.isSearchMode = false;

            if (this.selectedFilterTypes.length > 0) {
                this.fetchWithFilter(this.selectedFilterTypes);
            } else {
                this.restoreList();
                this.fetchInitialList(false);
            }
        },
        clearSearchQuery() {
            if (this.isFilterMode) {
                this.fetchWithFilter(this.selectedFilterTypes);
            } else {
                this.restoreList();
            }
        },
        restoreList() {
            this.list = [...this.originalList];
        },
        async dismissItem({ id, type, isModalTask = false }) {
            /*
            by default, the dismiss backend will be triggered in the notification-item component.
            this function is only for post-ajax ui.
            however, for modal tasks (convert to task), there is no notification-item component,
            thus the backend trigger needs to be done here.
            */
            if (isModalTask) {
                await NotificationService.dismiss([id]);
            }

            // remove from current list
            this.list = this.list.filter(item => item.id !== id);

            // remove from original list if any
            this.originalList = this.originalList.filter(
                item => item.id !== id
            );

            // update category count
            let newFilterTypes = [...this.filterTypes];
            let category = newFilterTypes.find(cat => cat.type === type);
            if (category) {
                category.count -= 1;
                this.filterTypes = newFilterTypes;
            }

            // remove from selection checked (if any)
            if (this.checkedItemIds.includes(id)) {
                this.checkedItemIds = this.checkedItemIds.filter(
                    itemId => itemId !== id
                );
            }
        },
        dismissAll() {
            if (this.checkedItemIds.length > 0) {
                this.proceedDismissAll(this.checkedItemIds); // no prompt, direct dismiss based on ids
            } else {
                this.isShowPromptDismissAll = true; // show prompt
            }
        },
        /**
         * determine which mode the "dismiss all" will proceed ie. filter | search | default mode
         */
        preDismissAll() {
            let ids = this.list.map(item => item.id);

            if (this.isFilterMode || this.isSearchMode) {
                this.proceedDismissAll(ids);
            } else {
                this.proceedDismissAll();
            }
        },
        /**
         * if the `ids` array has values, it will only dismiss the notifications related to those `ids`.
         * if the array is empty, all notifications will be dismissed.
         */
        async proceedDismissAll(ids = []) {
            if (this.list.length === 0) {
                return;
            }

            let isFullDismiss = ids.length === 0; // no ids passed

            this.isDismissingAll = true;
            await NotificationService.dismiss(ids);
            this.isDismissingAll = false;
            this.isShowPromptDismissAll = false;

            let updatedList = this.list.filter(item => !ids.includes(item.id)); // instant feedback, visually remove selected ids
            this.originalList = this.originalList.filter(
                item => !ids.includes(item.id)
            );

            if (this.isFilterMode) {
                if (updatedList.length > 0) {
                    this.fetchWithFilter(this.selectedFilterTypes); // if has remainder, re-fetch with filtered category
                    return;
                } else {
                    this.clearFilter(); // clear filter and do initial fetch
                    return;
                }
            }

            if (this.isSearchMode && !this.isFilterMode) {
                this.clearCheckboxSelection();
                this.search(this.searchValue); // re-fetch based on search query
                return;
            }

            // default mode -
            if (!isFullDismiss) {
                this.clearCheckboxSelection();
                this.list = updatedList;
                this.fetchInitialList(false);
                return;
            }

            // default mode - dismiss all
            this.fetchInitialList(true);
        },
        async convertToTask(notificationId) {
            this.preparingModalTaskId = notificationId;
            let item = this.list.find(not => not.id === notificationId);
            let meta;
            let metaparams = {};

            try {
                if (item.metadata) {
                    meta = JSON.parse(item.metadata);
                }
            } catch (error) {
                meta = {};
            }

            if (meta?.patient_id) {
                let res = await ServicePatient.getDetails(meta.patient_id, 0);
                if (res?.resources) {
                    metaparams.patient = {
                        id: res?.resources?.id,
                        name: res?.resources?.fullname
                    };
                }
            }

            if (meta?.client_id) {
                let res = await ServiceClient.getClient(meta.client_id, 0);
                if (res?.resource) {
                    let name = res?.resource?.firstname;
                    name += " " + res?.resource?.lastname ?? "";

                    metaparams.client = {
                        id: res?.resource?.id,
                        name: name
                    };
                }
            } else if (meta?.contact_id) {
                let res = await ServiceContact.getContactActor(
                    meta.contact_id,
                    { encoded: 0 }
                );
                if (res?.resource) {
                    metaparams.contact = {
                        id: res?.resource?.id,
                        name: res?.resource?.display_name
                    };
                }
            }

            this.$refs.modaltask.open({
                task: item.content,
                notificationId: item.id,
                notificationType: item.type.type,
                metadata: metaparams
            });

            this.preparingModalTaskId = false;
        },
        clearFilter(proceedFetch = true) {
            this.selectedFilterTypes = [];
            this.clearCheckboxSelection();

            if (proceedFetch) {
                this.fetchInitialList();
            }
        },
        async clearFilterSearch() {
            this.selectedFilterTypes = [];

            if (this.searchValue.length >= 3) {
                this.search(this.searchValue);
            } else if (this.searchValue === "") {
                await this.fetchInitialList(true);
                this.$refs.filterSearch.focusInputSearch();
            }
        },
        registerCheckItem(id) {
            if (this.checkedItemIds.includes(id)) {
                this.checkedItemIds = this.checkedItemIds.filter(
                    itemId => itemId !== id
                );
            } else {
                this.checkedItemIds.push(id);
            }
        },
        handleCheckAll(checked) {
            if (checked === true) {
                let arr = [];
                this.list.forEach(item => {
                    arr.push(item.id);
                });
                this.checkedItemIds = arr;
            } else {
                this.checkedItemIds = [];
            }

            this.isCheckedAll = checked;
        },
        startSearchMode() {
            this.isSearchMode = true;
            this.clearCheckboxSelection();
        },
        onLinkClicked() {
            this._toggleEnhancedLayoutMobileSideMenu(true);
            this.close();
        }
    }
};
</script>

<style lang="scss" scoped>
.app-notification-overlay {
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background: transparent;
    z-index: 1001;
    display: none;

    &.show {
        display: unset;
        position: fixed;
    }
}

.app-notification {
    background: #ffffff;
    border: 1px solid #ddd;
    position: fixed;
    top: 0;
    right: -500px;
    width: 500px;
    max-width: 100vw;
    z-index: 1002;
    bottom: 0;
    display: flex;
    flex-direction: column;
    transition: 0.3s;

    &.classic-layout {
        margin-top: 58px; // same as $top-bar-primary-height in src/module/layout/app-layout-classic.vue
    }
    &.enhanced-layout {
        margin-top: 52px; // same as $top-bar-secondary-height in src/module/layout/app-layout-enhanced.vue
    }

    &.show {
        transition: 0.3s;
        right: 0;
    }

    .title {
        padding: 1.5em;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #dbdbdb;
        background: #fff;
    }

    .content-holder {
        overflow-y: scroll;
        display: flex;
        flex-grow: 1;
        flex-direction: column;

        &.empty {
            align-items: center;
            justify-content: center;

            &.no-result {
                justify-content: unset;
            }

            .message {
                color: gray;
                text-align: center;
                padding: 0;
                margin: 0;
                cursor: default;
            }
        }

        .loading-next {
            color: gray;
            text-align: center;
            padding: 15px 0;
            font-size: 0.9em;
        }
    }

    .preloader {
        padding: 15px;
    }

    .skeleton {
        margin-bottom: 15px;
        height: 40px;
        border-radius: 3px;
        animation-duration: 7s;
        animation-fill-mode: forwards;
        animation-iteration-count: infinite;
        animation-name: loading;
        animation-timing-function: linear;
        background: #eeeeee;
        background: linear-gradient(
            to right,
            #f0f0f0 8%,
            #e4e4e4 30%,
            #f0f0f0 33%
        );
    }

    .just-received {
        text-align: center;
        padding: 0;
        height: 0;
        overflow: hidden;
        cursor: pointer;
        background: #e3f6f9;
        opacity: 0;
        transition: opacity 1s linear;

        &.show {
            opacity: 1;
            padding: 5px 0;
            overflow: unset;
            height: unset;
        }

        a {
            color: inherit;
        }

        span {
            display: block;
        }
    }

    .dismiss-all-confirm {
        display: none;
        background: #ffeaea;
        padding: 15px 25px;
        row-gap: 5px;
        align-items: center;

        &.show {
            display: flex;
        }

        button {
            outline: none;
            border: none;
            background: transparent;
            color: #7a0000;
            font-weight: bold;

            &:hover {
                text-decoration: underline;
            }
        }
    }
}

@media screen and (min-width: 992px) {
    .app-notification {
        &.classic-layout {
            margin-top: 64px; // same as $primary-top-bar-height in src/module/layout/app-layout-classic.vue
        }
    }
}
</style>
